import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import axios from 'axios';
import {
    ArrowLeft, Loader2, AlertTriangle, Lock, Calendar,
    Edit, Save, X, Cpu, Thermometer, Gauge, Droplets,
    CheckCircle, XCircle, Plus, Trash2, ExternalLink,
    FileText, Download, House, Upload, FileUp, Eye, Info, AlertOctagon, MapPinned
} from 'lucide-react';

// ✅ Импортируем новую иконку
import MeteringNodeIcon from './../../MeteringNodeIcon';

// ✅ Импортируем функцию генерации карты программирования
import { generateProgrammingCard, generateProgrammingCardTypeB } from './generateProgrammingCard';

// ============================================
// ✅ Система Toast-уведомлений
// ============================================
const ToastContext = createContext(null);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within ToastProvider');
    }
    return context;
};

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const showToast = useCallback((message, type = 'info', duration = 4000) => {
        const id = Date.now() + Math.random();
        const newToast = { id, message, type, duration };
        setToasts(prev => [...prev, newToast]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, duration);
        return id;
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const toast = {
        success: (message, duration) => showToast(message, 'success', duration),
        error: (message, duration) => showToast(message, 'error', duration || 6000),
        warning: (message, duration) => showToast(message, 'warning', duration),
        info: (message, duration) => showToast(message, 'info', duration),
    };

    return (
        <ToastContext.Provider value={toast}>
            {children}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </ToastContext.Provider>
    );
};

const ToastContainer = ({ toasts, removeToast }) => {
    if (toasts.length === 0) return null;
    return (
        <div className="fixed top-4 right-4 z-[100] space-y-2 max-w-md pointer-events-none">
            {toasts.map(t => (
                <ToastItem key={t.id} toast={t} onClose={() => removeToast(t.id)} />
            ))}
        </div>
    );
};

const ToastItem = ({ toast, onClose }) => {
    const { message, type } = toast;
    const config = {
        success: { bg: 'bg-green-500/10', border: 'border-green-500/30', icon: CheckCircle, iconColor: 'text-green-400', title: 'Успешно', textColor: 'text-green-200' },
        error: { bg: 'bg-red-500/10', border: 'border-red-500/30', icon: XCircle, iconColor: 'text-red-400', title: 'Ошибка', textColor: 'text-red-200' },
        warning: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', icon: AlertTriangle, iconColor: 'text-yellow-400', title: 'Внимание', textColor: 'text-yellow-200' },
        info: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', icon: Info, iconColor: 'text-blue-400', title: 'Информация', textColor: 'text-blue-200' }
    };
    const { bg, border, icon: Icon, iconColor, title, textColor } = config[type] || config.info;
    return (
        <div className={`${bg} ${border} border backdrop-blur-sm rounded-lg shadow-xl p-4 flex items-start gap-3 pointer-events-auto`}
            style={{ animation: 'slideInRight 0.3s ease-out' }}>
            <Icon className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} />
            <div className="flex-1 min-w-0">
                <div className={`font-semibold text-sm ${textColor}`}>{title}</div>
                <div className="text-slate-300 text-sm mt-1 break-words">{message}</div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white transition p-1 rounded hover:bg-slate-700/50 flex-shrink-0" title="Закрыть">
                <X className="w-4 h-4" />
            </button>
        </div>
    );
};

// ============================================
// ✅ Маппинг типов устройств
// ============================================
const DEVICE_TYPE_MAP = {
    'Aksioma01': 'Аксиома-01',
    'Aksioma03': 'Аксиома-03',
    'Karat307': 'Карат-307'
};


// ============================================
// ✅ Главный компонент
// ============================================
const HousePage = ({ city, street, house, onBack, isDesktop = false, onSelectDevice, onSelect }) => {

    const houseNumber = house.replace('Дом ', '').trim();

    const toast = useToast();
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const canChangeHouses = userInfo.canChangeHousesYY === 1; // управление узлами учёта (добавление/изменение/удаление узлов и их состава)
    const [houseData, setHouseData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [editPlombNode, setEditPlombNode] = useState(null);
    const [editDevice, setEditDevice] = useState(null);
    const [nodeFormModal, setNodeFormModal] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [createDeviceModal, setCreateDeviceModal] = useState(null);
    const [deleteDeviceConfirm, setDeleteDeviceConfirm] = useState(null);
    const [programmingCardModal, setProgrammingCardModal] = useState(null);
    const [uploadActModal, setUploadActModal] = useState(null);
    const [viewActsModal, setViewActsModal] = useState(null);
    const [savedViewActsModal, setSavedViewActsModal] = useState(null);

    useEffect(() => { loadHouse(); }, [city, street, houseNumber]);


    const handleGenerateProgrammingCardTypeB = async (node) => {
        const success = await generateProgrammingCardTypeB(node, city, street, house, toast);
        if (success) setProgrammingCardModal(null);
    };

    const loadHouse = async () => {

        console.log("userInfo = ", userInfo);

        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';
            const response = await axios.get(
                `${API_URL}/api/houses/${encodeURIComponent(city)}/${encodeURIComponent(street)}/${encodeURIComponent(houseNumber)}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.data) { setError('Дом не найден в базе данных'); return; }
            setHouseData(response.data);
        } catch (err) {
            console.error('❌ Ошибка загрузки дома:', err);
            if (err.response) {
                switch (err.response.status) {
                    case 404: toast.error('Дом не найден в базе данных'); break;
                    case 401: toast.error('Необходима авторизация'); break;
                    case 500: toast.error('Ошибка сервера'); break;
                    default: toast.error(err.response.data?.message || `Ошибка ${err.response.status}`);
                }
            } else if (err.request) {
                toast.error('Не удалось связаться с сервером');
            } else {
                toast.error(err.message || 'Не удалось загрузить данные дома');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleGenerateProgrammingCard = async (node) => {
        const success = await generateProgrammingCard(node, city, street, house, toast);
        if (success) setProgrammingCardModal(null);
    };

    const handleSavePlomb = async (nodeId, data) => {
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';
            await axios.put(`${API_URL}/api/houses/nodes/${nodeId}/plomb`, data,
                { headers: { 'Authorization': `Bearer ${token}` } });
            setEditPlombNode(null);
            loadHouse();
            toast.success('Опломбирование сохранено');
        } catch (err) {
            console.error('Ошибка сохранения опломбирования:', err);
            toast.error('Не удалось сохранить опломбирование');
        }
    };

    const handleSaveDevice = async (type, deviceId, data) => {
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';
            let endpoint;
            switch (type) {
                case 'heat-computer': endpoint = `/api/houses/heat-computers/${deviceId}`; break;
                case 'temp-sensor': endpoint = `/api/houses/temperature-sensors/${deviceId}`; break;
                case 'pressure-sensor': endpoint = `/api/houses/pressure-sensors/${deviceId}`; break;
                case 'flow-meter': endpoint = `/api/houses/flow-meters/${deviceId}`; break;
                default: return;
            }
            await axios.put(`${API_URL}${endpoint}`, data, { headers: { 'Authorization': `Bearer ${token}` } });
            setEditDevice(null);
            loadHouse();
            toast.success('Данные устройства сохранены');
        } catch (err) {
            console.error('Ошибка сохранения устройства:', err);
            toast.error('Не удалось сохранить данные устройства');
        }
    };

    const handleCreateNode = async (data) => {
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';
            await axios.post(
                // ✅ ИСПРАВЛЕНО: используется houseNumber (который уже вычислен как house.replace('Дом ', '').trim())
                `${API_URL}/api/houses/${encodeURIComponent(city)}/${encodeURIComponent(street)}/${encodeURIComponent(houseNumber)}/nodes`,
                data, { headers: { 'Authorization': `Bearer ${token}` } });
            setNodeFormModal(null);
            loadHouse();
            toast.success('Узел учёта создан');
        } catch (err) {
            console.error('Ошибка создания узла:', err);
            toast.error(err.response?.data?.message || 'Не удалось создать узел учёта');
        }
    };

    const handleUpdateNode = async (nodeId, data) => {
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';
            await axios.put(`${API_URL}/api/houses/nodes/${nodeId}`, data,
                { headers: { 'Authorization': `Bearer ${token}` } });
            setNodeFormModal(null);
            loadHouse();
            toast.success('Узел учёта обновлён');
        } catch (err) {
            console.error('Ошибка обновления узла:', err);
            toast.error(err.response?.data?.message || 'Не удалось обновить узел учёта');
        }
    };

    const handleDeleteNode = async (nodeId) => {
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';
            await axios.delete(`${API_URL}/api/houses/nodes/${nodeId}`,
                { headers: { 'Authorization': `Bearer ${token}` } });
            setDeleteConfirm(null);
            loadHouse();
            toast.success('Узел учёта удалён');
        } catch (err) {
            console.error('Ошибка удаления узла:', err);
            toast.error(err.response?.data?.message || 'Не удалось удалить узел учёта');
        }
    };

    const handleCreateDevice = async (nodeId, type, data) => {
        try {
            // 1. Создаем глубокую копию данных, чтобы не ломать исходный state
            const payload = JSON.parse(JSON.stringify(data));

            // 2. ЖЕСТКАЯ ВАЛИДАЦИЯ И ПРИВЕДЕНИЕ ТИПОВ
            // Гарантируем, что factoryNumber — это строка или null
            if (payload.factoryNumber !== undefined && payload.factoryNumber !== null) {
                payload.factoryNumber = String(payload.factoryNumber).trim();
            } else {
                payload.factoryNumber = null; // Или '', если бэкенд не принимает null
            }

            // Гарантируем, что deviceType и deviceName — строки (как требует ваш C# код)
            payload.deviceType = payload.deviceType ? String(payload.deviceType).trim() : '';
            payload.deviceName = payload.deviceName ? String(payload.deviceName).trim() : '';

            // 3. Исправление дат (из прошлого шага), если они есть в payload
            const dateFields = ['lastEditTime', 'verificationDate', 'oplombirovanDate', 'verificationValidUntil'];
            dateFields.forEach(field => {
                if (payload[field] && typeof payload[field] === 'string' && payload[field].includes('.')) {
                    const [datePart] = payload[field].split(' ');
                    const [day, month, year] = datePart.split('.');
                    payload[field] = `${year}-${month}-${day}`; // Преобразуем в ISO
                }
            });

            // 🔍 ДЛЯ ОТЛАДКИ: Раскомментируйте эту строку, чтобы видеть в консоли, что именно улетает на сервер
            // console.log("Отправляемый JSON payload:", JSON.stringify(payload, null, 2));

            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';
            let endpoint;

            switch (type) {
                case 'heat-computer': endpoint = `/api/houses/nodes/${nodeId}/heat-computers`; break;
                case 'temp-sensor': endpoint = `/api/houses/nodes/${nodeId}/temperature-sensors`; break;
                case 'pressure-sensor': endpoint = `/api/houses/nodes/${nodeId}/pressure-sensors`; break;
                case 'flow-meter': endpoint = `/api/houses/nodes/${nodeId}/flow-meters`; break;
                default: return;
            }

            await axios.post(`${API_URL}${endpoint}`, payload, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            setCreateDeviceModal(null);
            loadHouse();
            toast.success('Устройство создано');
        } catch (err) {
            console.error('Ошибка создания устройства:', err);

            // Улучшенный вывод ошибки: покажет детали валидации от ASP.NET Core
            let errorMsg = 'Не удалось создать устройство';
            if (err.response?.data?.errors) {
                // Преобразуем объект ошибок в читаемую строку
                errorMsg = Object.values(err.response.data.errors).flat().join('; ');
            } else if (err.response?.data?.message) {
                errorMsg = err.response.data.message;
            }

            toast.error(errorMsg);
        }
    };

    const handleDeleteDevice = async (type, deviceId) => {
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';
            let endpoint;
            switch (type) {
                case 'heat-computer': endpoint = `/api/houses/heat-computers/${deviceId}`; break;
                case 'temp-sensor': endpoint = `/api/houses/temperature-sensors/${deviceId}`; break;
                case 'pressure-sensor': endpoint = `/api/houses/pressure-sensors/${deviceId}`; break;
                case 'flow-meter': endpoint = `/api/houses/flow-meters/${deviceId}`; break;
                default: return;
            }
            await axios.delete(`${API_URL}${endpoint}`, { headers: { 'Authorization': `Bearer ${token}` } });
            setDeleteDeviceConfirm(null);
            loadHouse();
            toast.success('Устройство удалено');
        } catch (err) {
            console.error('Ошибка удаления устройства:', err);
            toast.error(err.response?.data?.message || 'Не удалось удалить устройство');
        }
    };

    const handleGoToDevice = (heatComputer) => {
        if (!onSelectDevice || !heatComputer) return;
        const deviceTypeRu = DEVICE_TYPE_MAP[heatComputer.deviceType] || heatComputer.deviceType;
        const houseNumber = house.replace('Дом ', '').trim();
        const device = {
            ...heatComputer,
            name: heatComputer.deviceName,
            deviceType: deviceTypeRu,
            fullName: `${deviceTypeRu} ${heatComputer.deviceName}`,
            city, street, houseNumber, status: 0
        };
        onSelectDevice({ type: 'device', data: device });
    };

    const handleUploadAct = async (nodeId, actType, file) => {
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';
            const formData = new FormData();
            formData.append('file', file);
            await axios.post(`${API_URL}/api/houses/nodes/${nodeId}/acts/${actType}`, formData, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
            });
            toast.success('Документ успешно загружен');
        } catch (err) {
            console.error('Ошибка загрузки документа:', err);

            let errorMessage = 'Не удалось загрузить документ';
            if (err.response?.data) {
                if (err.response.data.message) {
                    errorMessage = err.response.data.message;
                } else if (err.response.data.detail) {
                    errorMessage = err.response.data.detail;
                } else if (err.response.data.errors) {
                    const errors = err.response.data.errors;
                    const firstKey = Object.keys(errors)[0];
                    errorMessage = errors[firstKey][0];
                } else if (typeof err.response.data === 'string') {
                    errorMessage = err.response.data;
                }
            } else if (err.message) {
                errorMessage = err.message;
            }

            toast.error(errorMessage);
        }
    };

    const handleDeleteAct = async (actId, nodeId) => {
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';
            await axios.delete(`${API_URL}/api/houses/nodes/acts/${actId}`,
                { headers: { 'Authorization': `Bearer ${token}` } });
            toast.success('Документ удалён');
            if (viewActsModal) {
                const response = await axios.get(`${API_URL}/api/houses/nodes/${nodeId}/acts`,
                    { headers: { 'Authorization': `Bearer ${token}` } });
                setViewActsModal({ nodeId, acts: response.data });
            }
        } catch (err) {
            console.error('Ошибка удаления документа:', err);
            toast.error(err.response?.data?.message || 'Не удалось удалить документ');
        }
    };

    const handleViewActs = async (nodeId) => {
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';
            const response = await axios.get(`${API_URL}/api/houses/nodes/${nodeId}/acts`,
                { headers: { 'Authorization': `Bearer ${token}` } });
            setViewActsModal({ nodeId, acts: response.data });
        } catch (err) {
            console.error('Ошибка загрузки документов:', err);
            toast.error('Не удалось загрузить документы');
        }
    };

    if (isLoading) {
        return (
            <div className="h-full flex items-center justify-center">
                <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="h-full flex items-center justify-center p-4">
                <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    <span>{error}</span>
                </div>
            </div>
        );
    }

    const nodes = houseData?.meteringNodes || [];

    return (
        <div className="h-full flex flex-col">
            <div className="bg-slate-800 border-b border-slate-700 p-4">
                {!isDesktop && (
                    <div className="flex items-center gap-3 mb-3">
                        <button onClick={onBack}
                            className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition text-sm">
                            <ArrowLeft className="w-4 h-4" /> Назад
                        </button>
                    </div>
                )}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="flex items-center gap-2 text-lg sm:text-xl font-bold text-white">
                            <House className="w-6 h-6 shrink-0" />
                            <span>{house}</span>
                        </h2>

                        <div className="flex items-center gap-2 text-slate-400 text-sm flex-wrap mt-1">
                            <span>{city},</span>
                            <button
                                onClick={() => {
                                    if (onSelect) {
                                        onSelect({ type: 'street', data: { city, street } });
                                    } else {
                                        console.warn('⚠️ Не передан обработчик onSelect для перехода на улицу');
                                    }
                                }}
                                className="flex items-center gap-1 text-cyan-400 hover:text-cyan-300 hover:underline transition-colors rounded px-1 -ml-1"
                                title="Перейти к списку домов на этой улице"
                            >
                                <MapPinned className="w-3.5 h-3.5" />
                                <span>{street}</span>
                            </button>
                        </div>

                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {canChangeHouses && nodes.length > 0 && (
                            <button
                                onClick={() => setProgrammingCardModal({ node: nodes.length === 1 ? nodes[0] : null })}
                                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium transition">
                                <FileText className="w-4 h-4" /> Карта программирования
                            </button>
                        )}
                        {canChangeHouses && (
                            <button onClick={() => setNodeFormModal({ mode: 'create' })}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium transition">
                                <Plus className="w-4 h-4" /> Добавить узел
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-6 space-y-4">
                {nodes.length === 0 ? (
                    <div className="bg-slate-800 rounded-xl p-12 border border-slate-700 text-center">
                        <MeteringNodeIcon className="w-16 h-16 text-slate-600 mx-auto mb-3" />
                        <p className="text-slate-400 mb-4">В этом доме нет узлов учёта</p>
                        {canChangeHouses && (
                            <button onClick={() => setNodeFormModal({ mode: 'create' })}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium transition">
                                <Plus className="w-4 h-4" /> Создать первый узел
                            </button>
                        )}
                    </div>
                ) : (
                    nodes.map(node => (
                        <MeteringNodeCard
                            key={node.id}
                            node={node}
                            canChangeHouses={canChangeHouses}
                            onEditPlomb={() => setEditPlombNode(node)}
                            onEditNode={() => setNodeFormModal({ mode: 'edit', node })}
                            onDeleteNode={() => setDeleteConfirm({ nodeId: node.id, nodeName: node.name })}
                            onEditDevice={(type, data) => setEditDevice({ type, data })}
                            onCreateDevice={(type) => setCreateDeviceModal({ nodeId: node.id, type })}
                            onDeleteDevice={(type, deviceId, name, specificType) => setDeleteDeviceConfirm({ type, deviceId, deviceName: name, specificType })}
                            onGoToDevice={handleGoToDevice}
                            onGenerateCard={() => setProgrammingCardModal({ node })}
                            onViewActs={() => handleViewActs(node.id)}
                        />
                    ))
                )}
            </div>

            {editPlombNode && (
                <EditPlombModal node={editPlombNode} onClose={() => setEditPlombNode(null)}
                    onSave={(data) => handleSavePlomb(editPlombNode.id, data)} />
            )}

            {editDevice && (
                <EditDeviceModal type={editDevice.type} device={editDevice.data}
                    onClose={() => setEditDevice(null)}
                    onSave={(data) => handleSaveDevice(editDevice.type, editDevice.data.id, data)} />
            )}

            {nodeFormModal && (
                <NodeFormModal mode={nodeFormModal.mode} node={nodeFormModal.node}
                    onClose={() => setNodeFormModal(null)}
                    onSave={(data) => {
                        if (nodeFormModal.mode === 'create') handleCreateNode(data);
                        else handleUpdateNode(nodeFormModal.node.id, data);
                    }} />
            )}

            {deleteConfirm && (
                <DeleteConfirmModal nodeName={deleteConfirm.nodeName}
                    onClose={() => setDeleteConfirm(null)}
                    onConfirm={() => handleDeleteNode(deleteConfirm.nodeId)} />
            )}

            {createDeviceModal && (
                <CreateDeviceModal
                    type={createDeviceModal.type}
                    city={city}
                    street={street}
                    house={house}
                    onClose={() => setCreateDeviceModal(null)}
                    onSave={(data) => handleCreateDevice(createDeviceModal.nodeId, createDeviceModal.type, data)}
                />
            )}

            {deleteDeviceConfirm && (
                <DeleteDeviceConfirmModal
                    deviceName={deleteDeviceConfirm.deviceName}
                    deviceType={deleteDeviceConfirm.specificType}
                    sectionType={deleteDeviceConfirm.type}
                    onClose={() => setDeleteDeviceConfirm(null)}
                    onConfirm={() => handleDeleteDevice(deleteDeviceConfirm.type, deleteDeviceConfirm.deviceId)}
                />
            )}

            {programmingCardModal && (
                <ProgrammingCardModal
                    nodes={nodes}
                    selectedNode={programmingCardModal.node}
                    onClose={() => setProgrammingCardModal(null)}
                    onGenerate={handleGenerateProgrammingCard}
                    onGenerateTypeB={handleGenerateProgrammingCardTypeB} />
            )}

            {uploadActModal && (
                <UploadActModal actType={uploadActModal.actType}
                    onClose={() => {
                        setUploadActModal(null);
                        if (savedViewActsModal) {
                            handleViewActs(savedViewActsModal.nodeId);
                            setSavedViewActsModal(null);
                        }
                    }}
                    onUpload={async (file) => {
                        await handleUploadAct(uploadActModal.nodeId, uploadActModal.actType, file);
                        setUploadActModal(null);
                        if (savedViewActsModal) {
                            await handleViewActs(savedViewActsModal.nodeId);
                            setSavedViewActsModal(null);
                        }
                    }} />
            )}

            {viewActsModal && (
                <ViewActsModal nodeId={viewActsModal.nodeId} acts={viewActsModal.acts}
                    canChangeHouses={canChangeHouses}
                    onClose={() => setViewActsModal(null)}
                    onUploadAct={(actType) => {
                        setSavedViewActsModal(viewActsModal);
                        setViewActsModal(null);
                        setUploadActModal({ nodeId: viewActsModal.nodeId, actType });
                    }}
                    onDeleteAct={(actId) => handleDeleteAct(actId, viewActsModal.nodeId)} />
            )}
        </div>
    );
};

// ============================================
// ✅ Карточка узла учёта
// ============================================
const MeteringNodeCard = ({ node, canChangeHouses, onEditPlomb, onEditNode, onDeleteNode, onEditDevice, onCreateDevice, onDeleteDevice, onGoToDevice, onGenerateCard, onViewActs }) => {

    const getPlombStatus = () => {
        if (!node.isOplombirovan || !node.oplombirovanValidUntil) return null;
        const parts = node.oplombirovanValidUntil.split('.');
        if (parts.length !== 3) return null;
        const [day, month, year] = parts;
        const validUntilDate = new Date(`${year}-${month}-${day}`);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        validUntilDate.setHours(0, 0, 0, 0);
        const diffTime = validUntilDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays < 0) {
            return { status: 'expired', text: `до ${node.oplombirovanValidUntil}`, colorClass: 'bg-red-500/20 text-red-400' };
        } else if (diffDays <= 10) {
            const dayWord = diffDays === 1 ? 'день' : (diffDays >= 2 && diffDays <= 4 ? 'дня' : 'дней');
            return {
                status: 'warning',
                text: `до ${node.oplombirovanValidUntil} (осталось ${diffDays} ${dayWord})`,
                colorClass: 'bg-yellow-500/20 text-yellow-400'
            };
        }
        return {
            status: 'ok',
            text: `до ${node.oplombirovanValidUntil}`,
            colorClass: 'bg-green-600/20 text-green-400'
        };
    };

    const plombStatus = getPlombStatus();

    return (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            <div className="bg-slate-700/50 px-4 py-3 border-b border-slate-700 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <MeteringNodeIcon className="w-6 h-6 text-blue-400" />
                    <h3 className="text-white font-semibold">{node.name}</h3>
                    {node.description && <span className="text-slate-400 text-xs ml-2">— {node.description}</span>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {node.isOplombirovan ? (
                        <span className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${plombStatus?.colorClass || 'bg-green-600/20 text-green-400'}`}>
                            <Lock className="w-3 h-3" /> Опломбирован
                            {plombStatus ? (
                                <span className="ml-1 font-medium">{plombStatus.text}</span>
                            ) : (
                                node.oplombirovanValidUntil && <span className="ml-1">до {node.oplombirovanValidUntil}</span>
                            )}
                        </span>
                    ) : (
                        <span className="px-2 py-1 bg-slate-600/20 text-slate-400 rounded text-xs">Не опломбирован</span>
                    )}
                    {canChangeHouses && (
                        <>
                            <button onClick={onViewActs}
                                className="p-1.5 bg-orange-600 hover:bg-orange-500 text-white rounded transition"
                                title="Просмотр актов ввода/вывода в эксплуатацию">
                                <FileUp className="w-4 h-4" />
                            </button>
                            <button onClick={onGenerateCard}
                                className="p-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded transition"
                                title="Сформировать карту программирования">
                                <FileText className="w-4 h-4" />
                            </button>
                            <button onClick={onEditPlomb}
                                className="p-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded transition"
                                title="Изменить опломбирование">
                                <Lock className="w-4 h-4" />
                            </button>
                            <button onClick={onEditNode}
                                className="p-1.5 bg-yellow-600 hover:bg-yellow-500 text-white rounded transition"
                                title="Редактировать узел">
                                <Edit className="w-4 h-4" />
                            </button>
                            <button onClick={onDeleteNode}
                                className="p-1.5 bg-red-600 hover:bg-red-500 text-white rounded transition"
                                title="Удалить узел">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="p-4 space-y-4">
                <DeviceSection
                    title="Тепловычислитель"
                    icon={Cpu}
                    color="cyan"
                    device={node.heatComputer}
                    canChangeHouses={canChangeHouses}
                    onEdit={() => node.heatComputer && onEditDevice('heat-computer', node.heatComputer)}
                    onCreate={() => onCreateDevice('heat-computer')}
                    onDelete={() => node.heatComputer && onDeleteDevice('heat-computer', node.heatComputer.id, node.heatComputer.displayName || node.heatComputer.deviceName, node.heatComputer.deviceType)}
                    onGoToDevice={() => node.heatComputer && onGoToDevice(node.heatComputer)}
                />

                <DeviceListSection
                    title="Датчики температуры"
                    icon={Thermometer}
                    color="red"
                    devices={node.temperatureSensors}
                    canChangeHouses={canChangeHouses}
                    onEdit={(sensor) => onEditDevice('temp-sensor', sensor)}
                    onCreate={() => onCreateDevice('temp-sensor')}
                    onDelete={(sensor) => onDeleteDevice('temp-sensor', sensor.id, sensor.position || sensor.displayName, 'temp-sensor')}
                />

                <DeviceListSection
                    title="Датчики давления"
                    icon={Gauge}
                    color="purple"
                    devices={node.pressureSensors}
                    canChangeHouses={canChangeHouses}
                    onEdit={(sensor) => onEditDevice('pressure-sensor', sensor)}
                    onCreate={() => onCreateDevice('pressure-sensor')}
                    onDelete={(sensor) => onDeleteDevice('pressure-sensor', sensor.id, sensor.position || sensor.displayName, 'pressure-sensor')}
                />

                <DeviceListSection
                    title="Датчики объёмного расхода"
                    icon={Droplets}
                    color="blue"
                    devices={node.flowMeters}
                    canChangeHouses={canChangeHouses}
                    onEdit={(meter) => onEditDevice('flow-meter', meter)}
                    onCreate={() => onCreateDevice('flow-meter')}
                    onDelete={(meter) => onDeleteDevice('flow-meter', meter.id, meter.position || meter.displayName, 'flow-meter')}
                />
            </div>
        </div>
    );
};

// ============================================
// ✅ Секция одиночного устройства (Тепловычислитель)
// ============================================
const DeviceSection = ({ title, icon: Icon, color, device, canChangeHouses, onEdit, onCreate, onDelete, onGoToDevice }) => {
    const colorClasses = {
        cyan: 'border-cyan-500/30 text-cyan-400',
        red: 'border-red-500/30 text-red-400',
        purple: 'border-purple-500/30 text-purple-400',
        blue: 'border-blue-500/30 text-blue-400'
    };
    const [borderColor, textColor] = (colorClasses[color] || colorClasses.cyan).split(' ');

    const isVerificationExpired = () => {
        if (!device?.verificationValidUntil) return false;
        const validUntil = new Date(device.verificationValidUntil.split('.').reverse().join('-'));
        return validUntil < new Date();
    };

    return (
        <div className={`border ${borderColor} rounded-lg overflow-hidden`}>
            <div className={`bg-slate-800/50 px-3 py-2 border-b ${borderColor} flex items-center justify-between`}>
                <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${textColor}`} />
                    <h4 className="text-white font-medium text-sm">{title}</h4>
                </div>
                <div className="flex items-center gap-1">
                    {device && (
                        <>
                            {onGoToDevice && (
                                <button onClick={onGoToDevice}
                                    className="p-1 bg-indigo-600 hover:bg-indigo-500 text-white rounded transition"
                                    title="Открыть устройство">
                                    <ExternalLink className="w-3 h-3" />
                                </button>
                            )}
                            {canChangeHouses && (
                                <>
                                    <button onClick={onEdit}
                                        className="p-1 bg-blue-600 hover:bg-blue-500 text-white rounded transition"
                                        title="Редактировать">
                                        <Edit className="w-3 h-3" />
                                    </button>
                                    <button onClick={onDelete}
                                        className="p-1 bg-red-600 hover:bg-red-500 text-white rounded transition"
                                        title="Удалить">
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </>
                            )}
                        </>
                    )}
                    {!device && canChangeHouses && (
                        <button onClick={onCreate}
                            className="p-1 bg-green-600 hover:bg-green-500 text-white rounded transition"
                            title="Добавить">
                            <Plus className="w-3 h-3" />
                        </button>
                    )}
                </div>
            </div>
            {device ? (
                <div className="p-3 space-y-1 text-sm">
                    {/* ✅ Переход при нажатии на "Исполнение (Модель)" со значком оставлен */}
                    {onGoToDevice ? (
                        <button
                            onClick={onGoToDevice}
                            className="w-full flex items-center justify-between hover:bg-slate-700/30 transition rounded px-0 py-1 -mx-0 group text-left"
                            title="Перейти к устройству"
                        >
                            <span className="text-slate-400 text-sm">Исполнение (Модель):</span>
                            <div className="flex items-center gap-2">
                                <span className="text-white text-sm font-medium text-right">
                                    {device.deviceModel || DEVICE_TYPE_MAP[device.deviceType] || device.deviceType || '—'}
                                </span>
                                <ExternalLink className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0 opacity-60 group-hover:opacity-100 transition" />
                            </div>
                        </button>
                    ) : (
                        <InfoLine label="Исполнение (Модель)" value={device.deviceModel || DEVICE_TYPE_MAP[device.deviceType] || device.deviceType || '—'} />
                    )}

                    <InfoLine label="Описание" value={device.displayName} />
                    <InfoLine label="Заводской номер" value={device.factoryNumber} />
                    <InfoLine label="Дата выпуска" value={device.releaseDate} />
                    <InfoLine label="Дата поверки" value={device.verificationDate} />
                    <InfoLine label="Поверка до" value={device.verificationValidUntil} highlight={isVerificationExpired()} />
                    <InfoLine label="CRC16 настроек" value={device.crc16} />
                    <InfoLine label="Дата посл. изм. настроек" value={device.lastSettingsChange} />
                    <InfoLine label="Перемычка" value={device.jumperPosition} />

                    {device.algorithmFormula && (
                        <div className="flex items-start justify-between py-1">
                            <span className="text-slate-400 text-sm flex-shrink-0 mr-4">Алгоритм учета:</span>
                            <span className="text-white text-sm font-medium text-right whitespace-pre-line break-words">
                                {device.algorithmFormula}
                            </span>
                        </div>
                    )}

                    <InfoLine label="Параметр холодного источника" value={device.coldSourceParam} />
                    <InfoLine label="Глубина архивов" value={device.archiveDepth} />
                </div>
            ) : (
                <div className="p-3 text-center text-slate-500 text-sm">Не добавлен</div>
            )}
        </div>
    );
};

// ============================================
// ✅ Секция списка устройств
// ============================================
const DeviceListSection = ({ title, icon: Icon, color, devices, canChangeHouses, onEdit, onCreate, onDelete }) => {
    const colorClasses = {
        cyan: 'border-cyan-500/30 text-cyan-400',
        red: 'border-red-500/30 text-red-400',
        purple: 'border-purple-500/30 text-purple-400',
        blue: 'border-blue-500/30 text-blue-400'
    };
    const [borderColor, textColor] = (colorClasses[color] || colorClasses.cyan).split(' ');

    return (
        <div className={`border ${borderColor} rounded-lg overflow-hidden`}>
            <div className={`bg-slate-800/50 px-3 py-2 border-b ${borderColor} flex items-center justify-between`}>
                <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${textColor}`} />
                    <h4 className="text-white font-medium text-sm">{title}</h4>
                    <span className="text-slate-500 text-xs">({devices.length})</span>
                </div>
                {canChangeHouses && (
                    <button onClick={onCreate}
                        className="p-1 bg-green-600 hover:bg-green-500 text-white rounded transition"
                        title="Добавить">
                        <Plus className="w-3 h-3" />
                    </button>
                )}
            </div>
            <div className="divide-y divide-slate-700/50">
                {devices.length === 0 ? (
                    <div className="p-3 text-center text-slate-500 text-sm">Нет устройств</div>
                ) : (
                    devices.map(device => (
                        <DeviceItem
                            key={device.id}
                            device={device}
                            canChangeHouses={canChangeHouses}
                            onEdit={() => onEdit(device)}
                            onDelete={() => onDelete(device)}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

// ============================================
// ✅ Элемент устройства в списке (Датчик)
// ============================================
const DeviceItem = ({ device, canChangeHouses, onEdit, onDelete }) => {
    const isVerificationExpired = () => {
        if (!device.verificationValidUntil) return false;
        const validUntil = new Date(device.verificationValidUntil.split('.').reverse().join('-'));
        return validUntil < new Date();
    };

    return (
        <div className="px-3 py-2 hover:bg-slate-700/20 transition">
            <div className="flex items-center justify-between mb-1">
                <span className="text-white font-medium text-sm">
                    {device.position || device.displayName || '—'}
                </span>
                <div className="flex items-center gap-2">
                    {isVerificationExpired() && (
                        <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded text-xs">Просрочена</span>
                    )}
                    {canChangeHouses && (
                        <>
                            <button onClick={onEdit}
                                className="p-1 bg-blue-600 hover:bg-blue-500 text-white rounded transition"
                                title="Редактировать">
                                <Edit className="w-3 h-3" />
                            </button>
                            <button onClick={onDelete}
                                className="p-1 bg-red-600 hover:bg-red-500 text-white rounded transition"
                                title="Удалить">
                                <Trash2 className="w-3 h-3" />
                            </button>
                        </>
                    )}
                </div>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-slate-400">
                {device.deviceType && <span>Тип: <span className="text-slate-300">{device.deviceType}</span></span>}
                {device.deviceModel && <span>Исполнение: <span className="text-slate-300">{device.deviceModel}</span></span>}
                <span>Поверка: <span className="text-slate-300">{device.verificationDate || '—'}</span></span>
                <span>Зав. номер: <span className="text-slate-300">{device.factoryNumber || '—'}</span></span>
                <span>До: <span className={isVerificationExpired() ? 'text-red-400' : 'text-slate-300'}>{device.verificationValidUntil || '—'}</span></span>
            </div>
            {device.specs && (
                <div className="mt-1 text-xs text-slate-400">
                    <span>Тех. характеристики: </span>
                    <span className="text-slate-300 whitespace-pre-line">{device.specs}</span>
                </div>
            )}
        </div>
    );
};

const InfoLine = ({ label, value, highlight = false }) => (
    <div className="flex items-center justify-between">
        <span className="text-slate-400">{label}:</span>
        <span className={highlight ? 'text-red-400 font-medium' : 'text-white'}>{value || '—'}</span>
    </div>
);

// ============================================
// ✅ Модальное окно загрузки акта / документа
// ============================================
const UploadActModal = ({ actType, onClose, onUpload }) => {
    const toast = useToast();
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = React.useRef(null);

    const actTypeNames = {
        'commissioning': 'Акт ввода в эксплуатацию',
        'decommissioning': 'Акт вывода из эксплуатации',
        'other': 'Документ'
    };

    const validateFile = (file) => {
        if (file.size > 10 * 1024 * 1024) {
            toast.warning('Размер файла не должен превышать 10 MB');
            return false;
        }
        return true;
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && validateFile(file)) setSelectedFile(file);
    };

    const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); if (!isDragging) setIsDragging(true); };
    const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
    const handleDrop = (e) => {
        e.preventDefault(); e.stopPropagation(); setIsDragging(false);
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (validateFile(file)) setSelectedFile(file);
        }
    };

    const handleZoneClick = () => fileInputRef.current?.click();
    const handleRemoveFile = (e) => {
        e.stopPropagation();
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleUpload = async () => {
        if (!selectedFile) { toast.warning('Выберите файл'); return; }
        setIsUploading(true);
        try { await onUpload(selectedFile); }
        finally { setIsUploading(false); }
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / 1024 / 1024).toFixed(2) + ' MB';
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
            <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-md">
                <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                        <Upload className="w-5 h-5 text-orange-400" />
                        Загрузка: {actTypeNames[actType]}
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>

                <div className="p-4 space-y-4">
                    <div>
                        <label className="block text-slate-400 text-xs mb-2">Выберите файл (макс. 10 MB)</label>
                        <div
                            onClick={handleZoneClick}
                            onDragEnter={handleDragEnter}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
                                ${isDragging ? 'border-orange-500 bg-orange-500/10 scale-[1.02]'
                                    : selectedFile ? 'border-green-500/50 bg-green-500/5 hover:border-green-500'
                                        : 'border-slate-600 hover:border-orange-500'}`}>

                            <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip,.rar"
                                onChange={handleFileChange} className="hidden" />

                            {selectedFile ? (
                                <div className="space-y-2">
                                    <FileText className="w-12 h-12 text-green-400 mx-auto" />
                                    <div>
                                        <p className="text-white font-medium text-sm truncate">{selectedFile.name}</p>
                                        <p className="text-slate-400 text-xs mt-1">{formatFileSize(selectedFile.size)}</p>
                                    </div>
                                    <button onClick={handleRemoveFile}
                                        className="inline-flex items-center gap-1 px-3 py-1 bg-red-600/20 hover:bg-red-600/30 text-red-300 rounded text-xs transition">
                                        <Trash2 className="w-3 h-3" /> Удалить
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <Upload className={`w-12 h-12 mx-auto transition-colors ${isDragging ? 'text-orange-400' : 'text-slate-500'}`} />
                                    <p className="text-slate-400 text-sm">
                                        {isDragging ? 'Отпустите файл для загрузки' : 'Перетащите файл сюда или нажмите для выбора'}
                                    </p>
                                    <p className="text-slate-500 text-xs">PDF, Word, Excel, изображения, архивы</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="px-4 py-3 border-t border-slate-700 flex justify-end gap-2">
                    <button onClick={onClose}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm">Отмена</button>
                    <button onClick={handleUpload} disabled={isUploading || !selectedFile}
                        className="px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">
                        {isUploading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Загрузка...</>)
                            : (<><Upload className="w-4 h-4" /> Загрузить</>)}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// ✅ Модальное окно просмотра актов
// ============================================
const ViewActsModal = ({ nodeId, acts, canChangeHouses, onClose, onUploadAct, onDeleteAct }) => {
    const toast = useToast();

    const handleDownload = async (actId, fileName) => {
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';
            const response = await fetch(`${API_URL}/api/houses/nodes/acts/${actId}/download`,
                { headers: { 'Authorization': `Bearer ${token}` } });
            if (!response.ok) throw new Error(`Ошибка ${response.status}: ${response.statusText}`);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url; link.download = fileName;
            document.body.appendChild(link); link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('❌ Ошибка скачивания акта:', err);
            toast.error('Не удалось скачать акт: ' + err.message);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / 1024 / 1024).toFixed(2) + ' MB';
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800 z-10">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                        <FileUp className="w-5 h-5 text-orange-400" /> Документы узла учёта
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>

                <div className="p-4 space-y-6">
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-white font-medium flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-400" /> Акты ввода в эксплуатацию
                            </h4>
                            {canChangeHouses && (
                                <button onClick={() => onUploadAct('commissioning')}
                                    className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white rounded-lg text-xs font-medium flex items-center gap-1">
                                    <Plus className="w-3 h-3" /> Загрузить
                                </button>
                            )}
                        </div>
                        {acts.commissioningActs.length === 0 ? (
                            <div className="bg-slate-900/50 rounded-lg p-4 text-center text-slate-500 text-sm">
                                Нет актов ввода в эксплуатацию
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {acts.commissioningActs.map(act => (
                                    <ActItem key={act.id} act={act} canChangeHouses={canChangeHouses}
                                        onDownload={handleDownload} onDelete={onDeleteAct}
                                        formatFileSize={formatFileSize} />
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-white font-medium flex items-center gap-2">
                                <XCircle className="w-4 h-4 text-red-400" /> Акты вывода из эксплуатации
                            </h4>
                            {canChangeHouses && (
                                <button onClick={() => onUploadAct('decommissioning')}
                                    className="px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white rounded-lg text-xs font-medium flex items-center gap-1">
                                    <Plus className="w-3 h-3" /> Загрузить
                                </button>
                            )}
                        </div>
                        {acts.decommissioningActs.length === 0 ? (
                            <div className="bg-slate-900/50 rounded-lg p-4 text-center text-slate-500 text-sm">
                                Нет актов вывода из эксплуатации
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {acts.decommissioningActs.map(act => (
                                    <ActItem key={act.id} act={act} canChangeHouses={canChangeHouses}
                                        onDownload={handleDownload} onDelete={onDeleteAct}
                                        formatFileSize={formatFileSize} />
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-white font-medium flex items-center gap-2">
                                <FileText className="w-4 h-4 text-blue-400" /> Другие документы
                            </h4>
                            {canChangeHouses && (
                                <button onClick={() => onUploadAct('other')}
                                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-medium flex items-center gap-1">
                                    <Plus className="w-3 h-3" /> Загрузить
                                </button>
                            )}
                        </div>
                        {(!acts.otherActs || acts.otherActs.length === 0) ? (
                            <div className="bg-slate-900/50 rounded-lg p-4 text-center text-slate-500 text-sm">
                                Нет других документов
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {acts.otherActs.map(act => (
                                    <ActItem key={act.id} act={act} canChangeHouses={canChangeHouses}
                                        onDownload={handleDownload} onDelete={onDeleteAct}
                                        formatFileSize={formatFileSize} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="px-4 py-3 border-t border-slate-700 flex justify-end sticky bottom-0 bg-slate-800">
                    <button onClick={onClose}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm">Закрыть</button>
                </div>
            </div>
        </div>
    );
};

const ActItem = ({ act, canChangeHouses, onDownload, onDelete, formatFileSize }) => {
    return (
        <div className="bg-slate-900/50 rounded-lg p-3 flex items-center gap-3 hover:bg-slate-900 transition">
            <FileText className="w-8 h-8 text-orange-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="text-white font-medium text-sm truncate">{act.fileName}</div>
                <div className="text-slate-500 text-xs mt-1">
                    {formatFileSize(act.fileSize)} • Загружен: {act.uploadDate}
                    {act.uploadedBy && ` • ${act.uploadedBy}`}
                </div>
            </div>
            <div className="flex items-center gap-2">
                <button onClick={() => onDownload(act.id, act.fileName)}
                    className="p-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded transition" title="Скачать">
                    <Download className="w-4 h-4" />
                </button>
                {canChangeHouses && (
                    <button onClick={() => onDelete(act.id)}
                        className="p-1.5 bg-red-600 hover:bg-red-500 text-white rounded transition" title="Удалить">
                        <Trash2 className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
};




// ============================================
// ✅ Модальное окно карты программирования
// ============================================
const ProgrammingCardModal = ({ nodes, selectedNode, onClose, onGenerate, onGenerateTypeB }) => {
    const toast = useToast();
    const [currentNode, setCurrentNode] = useState(selectedNode || (nodes.length === 1 ? nodes[0] : null));
    const [isGenerating, setIsGenerating] = useState(false);
    const [cardType, setCardType] = useState('A');

    const handleGenerate = async () => {
        if (!currentNode) { toast.warning('Выберите узел учёта'); return; }
        setIsGenerating(true);
        try {
            if (cardType === 'B') {
                await onGenerateTypeB(currentNode);
            } else {
                await onGenerate(currentNode);
            }
        }
        finally { setIsGenerating(false); }
    };

    const hc = currentNode?.heatComputer;

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800 z-10">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                        <FileText className="w-5 h-5 text-purple-400" /> Карта программирования
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>

                <div className="p-4 space-y-4">

                    {/* Выбор типа карты */}
                    <div className="flex gap-2 mb-2">
                        <button
                            onClick={() => setCardType('A')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition border ${cardType === 'A' ? 'bg-purple-600 border-purple-500 text-white' : 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600'
                                }`}
                        >
                            Тип A (Стандартная)
                        </button>
                        <button
                            onClick={() => setCardType('B')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition border ${cardType === 'B' ? 'bg-purple-600 border-purple-500 text-white' : 'bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600'
                                }`}
                        >
                            Тип B (Приложение А)
                        </button>
                    </div>


                    {nodes.length > 1 && (
                        <div>
                            <label className="block text-slate-400 text-xs mb-1">Узел учёта *</label>
                            <select value={currentNode?.id || ''}
                                onChange={(e) => {
                                    const node = nodes.find(n => n.id === parseInt(e.target.value));
                                    setCurrentNode(node || null);
                                }}
                                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm">
                                <option value="">— Выберите узел —</option>
                                {nodes.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
                            </select>
                        </div>
                    )}

                    {currentNode && (
                        <>
                            <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                                <h4 className="text-white font-medium text-sm mb-3">Тепловычислитель</h4>
                                {hc ? (
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div><span className="text-slate-400">Исполнение (Модель):</span> <span className="text-white">{hc.deviceModel || DEVICE_TYPE_MAP[hc.deviceType] || hc.deviceType || '—'}</span></div>
                                        <div><span className="text-slate-400">Описание:</span> <span className="text-white">{hc.displayName || hc.deviceName || '—'}</span></div>
                                        <div><span className="text-slate-400">Зав. номер:</span> <span className="text-white">{hc.factoryNumber || '—'}</span></div>
                                        <div><span className="text-slate-400">Дата выпуска:</span> <span className="text-white">{hc.releaseDate || '—'}</span></div>
                                        <div><span className="text-slate-400">CRC16:</span> <span className="text-white">{hc.crc16 || '—'}</span></div>
                                        <div><span className="text-slate-400">Перемычка:</span> <span className="text-white">{hc.jumperPosition || '—'}</span></div>
                                        <div><span className="text-slate-400">Архивы:</span> <span className="text-white">{hc.archiveDepth || '—'}</span></div>
                                        <div><span className="text-slate-400">Алгоритм:</span> <span className="text-white">{hc.algorithmFormula || '—'}</span></div>
                                    </div>
                                ) : (
                                    <div className="text-slate-500 text-xs">Тепловычислитель не добавлен</div>
                                )}
                            </div>

                            <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                                <h4 className="text-white font-medium text-sm mb-3">Датчики</h4>
                                <div className="text-xs text-slate-400">
                                    • Температуры: {currentNode.temperatureSensors.length}<br />
                                    • Давления: {currentNode.pressureSensors.length}<br />
                                    • Расходомеры: {currentNode.flowMeters.length}
                                </div>
                            </div>

                            <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                                <h4 className="text-white font-medium text-sm mb-3">Ответственные</h4>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div><span className="text-slate-400">Организация:</span> <span className="text-white">{currentNode.responsibleOrg || '—'}</span></div>
                                    <div><span className="text-slate-400">ФИО:</span> <span className="text-white">{currentNode.responsibleName || '—'}</span></div>
                                    <div><span className="text-slate-400">УК:</span> <span className="text-white">{currentNode.managementOrg || '—'}</span></div>
                                    <div><span className="text-slate-400">Директор:</span> <span className="text-white">{currentNode.managementName || '—'}</span></div>
                                </div>
                            </div>

                            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-xs text-yellow-200">
                                Будет сформирован документ Word (.docx) с картой программирования на основе введённых данных.
                            </div>
                        </>
                    )}
                </div>

                <div className="px-4 py-3 border-t border-slate-700 flex justify-end gap-2 sticky bottom-0 bg-slate-800">
                    <button onClick={onClose}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm">Отмена</button>
                    <button onClick={handleGenerate} disabled={isGenerating || !currentNode}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">
                        {isGenerating ? (<><Loader2 className="w-4 h-4 animate-spin" /> Формирование...</>)
                            : (<><Download className="w-4 h-4" /> Сформировать карту</>)}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// ✅ Шаблоны placeholder для разных типов датчиков
// ============================================
const DEVICE_PLACEHOLDERS = {
    'flow-meter': {
        type: 'Расходомер-счетчик электромагнитный',
        model: 'КАРАТ-551-40',
        specs: 'Вес импульса 1 л/импульс;\nQmin (Vmin) 0,18 м³/ч;\nQt1 (Vper) 0,45 м³/ч;\nQmax (Vmax) 45 м³/ч.'
    },
    'temp-sensor': {
        type: 'Комплект термометров сопротивления',
        model: 'КТПТР-06',
        specs: 'Температурный коэффициент 000391°С-1;\nНоминальное сопротивление 100 Ом;\ntmin 10°C; tmax 95°C.'
    },
    'pressure-sensor': {
        type: 'Преобразователь давления измерительный КАРАТ',
        model: 'СДВ-И-2,5-1,6-1,0-М-4-20 мА-DA422-0605-3',
        specs: 'Верхний предел измерения 1,6 МПа;\nВыходной сигнал 4-20 мА.'
    }
};


// ============================================
// ✅ Модальное окно редактирования устройства
// ============================================
const EditDeviceModal = ({ type, device, onClose, onSave }) => {
    const [displayName, setDisplayName] = useState(device.displayName || '');
    const [factoryNumber, setFactoryNumber] = useState(device.factoryNumber || '');
    const [verificationDate, setVerificationDate] = useState(
        device.verificationDate ? device.verificationDate.split('.').reverse().join('-') : ''
    );
    const [validUntil, setValidUntil] = useState(
        device.verificationValidUntil ? device.verificationValidUntil.split('.').reverse().join('-') : ''
    );
    const [position, setPosition] = useState(device.position || '');

    // Поля для датчиков
    const [sensorDeviceType, setSensorDeviceType] = useState(device.deviceType || '');
    const [sensorDeviceModel, setSensorDeviceModel] = useState(device.deviceModel || '');
    const [specs, setSpecs] = useState(device.specs || '');

    // Поля для тепловычислителя
    const [releaseDate, setReleaseDate] = useState(
        device.releaseDate ? device.releaseDate.split('.').reverse().join('-') : ''
    );
    const [crc16, setCrc16] = useState(device.crc16 || '');
    const [lastSettingsChange, setLastSettingsChange] = useState(
        device.lastSettingsChange ? device.lastSettingsChange.split('.').reverse().join('-') : ''
    );
    const [jumperPosition, setJumperPosition] = useState(device.jumperPosition || 'Изменения запрещены');
    const [algorithmFormula, setAlgorithmFormula] = useState(device.algorithmFormula || '');
    const [coldSourceParam, setColdSourceParam] = useState(device.coldSourceParam || '');
    const [archiveDepth, setArchiveDepth] = useState(device.archiveDepth || '');

    const typeNames = {
        'heat-computer': 'Тепловычислитель',
        'temp-sensor': 'Датчик температуры',
        'pressure-sensor': 'Датчик давления',
        'flow-meter': 'Расходомер'
    };

    const isHeatComputer = type === 'heat-computer';
    const isSensor = type !== 'heat-computer';
    const placeholders = DEVICE_PLACEHOLDERS[type] || {};

    const handleSave = () => {
        if (isHeatComputer) {
            onSave({
                displayName: displayName || null,
                factoryNumber: factoryNumber || null,
                verificationDate: verificationDate || null,
                verificationValidUntil: validUntil || null,
                releaseDate: releaseDate || null,
                crc16: crc16 || null,
                lastSettingsChange: lastSettingsChange || null,
                jumperPosition: jumperPosition || null,
                algorithmFormula: algorithmFormula || null,
                coldSourceParam: coldSourceParam || null,
                archiveDepth: archiveDepth || null
            });
        } else {
            onSave({
                factoryNumber: factoryNumber || null,
                verificationDate: verificationDate || null,
                verificationValidUntil: validUntil || null,
                position: position || null,
                deviceType: sensorDeviceType || null,
                deviceModel: sensorDeviceModel || null,
                specs: specs || null
            });
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800 z-10">
                    <h3 className="text-white font-semibold">
                        {typeNames[type]}: {device.position || device.displayName || device.deviceName || '—'}
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>

                <div className="p-4 space-y-4">
                    {isSensor ? (
                        <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                            <h4 className="text-white font-medium text-sm mb-3">Характеристики устройства</h4>
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-slate-400 text-xs mb-1">Тип</label>
                                        <input type="text" value={sensorDeviceType} onChange={(e) => setSensorDeviceType(e.target.value)}
                                            placeholder={placeholders.type || 'Тип устройства'}
                                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-slate-400 text-xs mb-1">Исполнение (Модель)</label>
                                        <input type="text" value={sensorDeviceModel} onChange={(e) => setSensorDeviceModel(e.target.value)}
                                            placeholder={placeholders.model || 'Модель устройства'}
                                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Переменная</label>
                                    <input type="text" value={position} onChange={(e) => setPosition(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-slate-400 text-xs mb-1">Заводской номер</label>
                                        <input type="text" value={factoryNumber} onChange={(e) => setFactoryNumber(e.target.value)}
                                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-slate-400 text-xs mb-1">Технические характеристики</label>
                                        <textarea value={specs} onChange={(e) => setSpecs(e.target.value)}
                                            placeholder={placeholders.specs || 'Технические характеристики'}
                                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm min-h-[100px] resize-y whitespace-pre-wrap" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-slate-400 text-xs mb-1">Дата поверки</label>
                                        <input
                                            type="date"
                                            value={verificationDate}
                                            onChange={(e) => setVerificationDate(e.target.value)}
                                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm 
                                               [&::-webkit-calendar-picker-indicator]:invert 
                                               [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-slate-400 text-xs mb-1">Поверка действительна до</label>
                                        <input
                                            type="date"
                                            value={validUntil}
                                            onChange={(e) => setValidUntil(e.target.value)}
                                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm
                                               [&::-webkit-calendar-picker-indicator]:invert
                                               [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // ✅ Форма для тепловычислителя (Тип, Исполнение и Наименование в системе скрыты)
                        <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                            <h4 className="text-white font-medium text-sm mb-3">Основная информация</h4>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Описание</label>
                                    <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Заводской номер</label>
                                    <input type="text" value={factoryNumber} onChange={(e) => setFactoryNumber(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Дата поверки</label>
                                    <input
                                        type="date"
                                        value={verificationDate}
                                        onChange={(e) => setVerificationDate(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm 
                                           [&::-webkit-calendar-picker-indicator]:invert 
                                           [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                    />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Поверка действительна до</label>
                                    <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm
                                           [&::-webkit-calendar-picker-indicator]:invert
                                           [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {isHeatComputer && (
                        <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                            <h4 className="text-white font-medium text-sm mb-3">Параметры программирования</h4>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Дата выпуска</label>
                                    <input type="date" value={releaseDate} onChange={(e) => setReleaseDate(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm
                                           [&::-webkit-calendar-picker-indicator]:invert 
                                           [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                    />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">CRC16 настроек</label>
                                    <input type="text" value={crc16} onChange={(e) => setCrc16(e.target.value)}
                                        placeholder="0x2DAC"
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Дата посл. изм. настроек</label>
                                    <input type="date" value={lastSettingsChange} onChange={(e) => setLastSettingsChange(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm
                                           [&::-webkit-calendar-picker-indicator]:invert 
                                           [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                    />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Перемычка</label>
                                    <select value={jumperPosition} onChange={(e) => setJumperPosition(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm">
                                        <option value="Изменения запрещены">Изменения запрещены</option>
                                        <option value="Изменения разрешены">Изменения разрешены</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Глубина архивов</label>
                                    <input type="text" value={archiveDepth} onChange={(e) => setArchiveDepth(e.target.value)}
                                        placeholder="4 года"
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                                </div>
                            </div>
                            <div className="space-y-3 mt-3">
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Алгоритм учета</label>
                                    <textarea
                                        value={algorithmFormula}
                                        onChange={(e) => setAlgorithmFormula(e.target.value)}
                                        placeholder={`dt=t1-t2\ndP=P1-P2\nM1=p1V1\nM2=p2V2\ndGm=M1-M2\nQ1=M1 * (t1-t2)`}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm min-h-[100px] resize-y whitespace-pre-wrap"
                                    />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Параметр холодного источника</label>
                                    <input type="text" value={coldSourceParam} onChange={(e) => setColdSourceParam(e.target.value)}
                                        placeholder="6,5 °C"
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="px-4 py-3 border-t border-slate-700 flex justify-end gap-2 sticky bottom-0 bg-slate-800">
                    <button onClick={onClose}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm">Отмена</button>
                    <button onClick={handleSave}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm flex items-center gap-2">
                        <Save className="w-4 h-4" /> Сохранить
                    </button>
                </div>
            </div>
        </div>
    );
};


// ============================================
// ✅ Модальное окно создания устройства (только выбор из существующих)
// ============================================
const CreateDeviceModal = ({ type, city, street, house, onClose, onSave }) => {
    const houseNumber = house.replace('Дом ', '').trim();

    const toast = useToast();
    const [deviceType, setDeviceType] = useState('');
    const [deviceModel, setDeviceModel] = useState('');
    const [deviceName, setDeviceName] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [factoryNumber, setFactoryNumber] = useState('');
    const [position, setPosition] = useState('');
    const [verificationDate, setVerificationDate] = useState('');
    const [validUntil, setValidUntil] = useState('');

    // Поля для датчиков
    const [sensorDeviceType, setSensorDeviceType] = useState('');
    const [sensorDeviceModel, setSensorDeviceModel] = useState('');
    const [sensorSpecs, setSensorSpecs] = useState('');

    // Поля для тепловычислителя
    const [releaseDate, setReleaseDate] = useState('');
    const [crc16, setCrc16] = useState('');
    const [lastSettingsChange, setLastSettingsChange] = useState('');
    const [jumperPosition, setJumperPosition] = useState('Изменения запрещены');
    const [algorithmFormula, setAlgorithmFormula] = useState('');
    const [coldSourceParam, setColdSourceParam] = useState('6,5 °C');
    const [archiveDepth, setArchiveDepth] = useState('4 года');

    const [availableDevices, setAvailableDevices] = useState([]);
    const [selectedDeviceIndex, setSelectedDeviceIndex] = useState(-1);
    const [isLoadingDevices, setIsLoadingDevices] = useState(false);

    const typeNames = {
        'heat-computer': 'Тепловычислитель',
        'temp-sensor': 'Датчик температуры',
        'pressure-sensor': 'Датчик давления',
        'flow-meter': 'Расходомер'
    };

    const positionOptions = {
        'temp-sensor': ['t1', 't2', 't3', 't4', 'tx', 'tok'],
        'pressure-sensor': ['P1', 'P2', 'P3', 'P4', 'Px'],
        'flow-meter': ['V1', 'V2', 'V3', 'V4', 'Vx']
    };

    const isHeatComputer = type === 'heat-computer';
    const isSensor = type !== 'heat-computer';
    const placeholders = DEVICE_PLACEHOLDERS[type] || {};

    useEffect(() => {
        if (isHeatComputer && city && street && houseNumber) {
            loadAvailableDevices();
        }
    }, [isHeatComputer, city, street, houseNumber]);

    // Автоматический выбор первого устройства при загрузке
    useEffect(() => {
        if (availableDevices.length > 0 && selectedDeviceIndex === -1) {
            setSelectedDeviceIndex(0);
            const selectedDevice = availableDevices[0];
            setDeviceType(selectedDevice.deviceType);
            setDeviceModel(selectedDevice.deviceModel || DEVICE_TYPE_MAP[selectedDevice.deviceType] || '');
            setDeviceName(selectedDevice.deviceName);
            setDisplayName(selectedDevice.displayName || '');
            setFactoryNumber(selectedDevice.deviceName || '');
            loadDeviceSettings(selectedDevice);
        }
    }, [availableDevices]);

    const loadAvailableDevices = async () => {
        setIsLoadingDevices(true);
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';
            const response = await axios.get(
                `${API_URL}/api/houses/${encodeURIComponent(city)}/${encodeURIComponent(street)}/${encodeURIComponent(houseNumber)}/devices`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            setAvailableDevices(response.data || []);
        } catch (err) {
            console.error('Ошибка загрузки списка устройств:', err);
        } finally {
            setIsLoadingDevices(false);
        }
    };

    const handleDeviceSelect = (e) => {
        const index = parseInt(e.target.value);
        setSelectedDeviceIndex(index);

        const selectedDevice = availableDevices[index];



        if (selectedDevice) {
            setDeviceType(selectedDevice.deviceType);
            setDeviceModel(selectedDevice.deviceModel || DEVICE_TYPE_MAP[selectedDevice.deviceType] || '');
            setDeviceName(selectedDevice.deviceName);
            setDisplayName(selectedDevice.displayName || '');
            setVerificationDate('');
            setValidUntil('');
            loadDeviceSettings(selectedDevice);
        }
    };

    const loadDeviceSettings = async (selectedDevice) => {
        try {
            console.log("selectedDevice: ", selectedDevice);

            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';

            let endpoint;
            switch (selectedDevice.deviceType) {
                case 'Aksioma01': endpoint = `/api/devices/aksioma01/${selectedDevice.deviceName}/info`; break;
                case 'Aksioma03': endpoint = `/api/devices/aksioma03/${selectedDevice.deviceName}/info`; break;
                case 'Karat307': endpoint = `/api/devices/karat307/${selectedDevice.deviceName}/info`; break;
                default: return;
            }

            const response = await axios.get(`${API_URL}${endpoint}`, { headers: { 'Authorization': `Bearer ${token}` } });
            autofillFromSettings(response.data, selectedDevice.deviceType);
        } catch (err) {
            console.error('Ошибка загрузки настроек устройства:', err);
            toast.warning('Не удалось загрузить настройки устройства');
        }
    };

    const autofillFromSettings = (settings, deviceType) => {
        console.log("settings: ", settings);

        // Универсальный помощник для приведения даты к формату "yyyy-MM-dd"
        const formatDate = (dateStr) => {
            if (!dateStr) return '';

            // Берем только часть до пробела или буквы 'T' (отсекаем время)
            const datePart = dateStr.split(/[ T]/)[0];

            // Если формат уже ГГГГ-ММ-ДД (например, "2025-08-26")
            if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
                return datePart;
            }

            // Если формат ДД.ММ.ГГГГ (например, "23.12.2022")
            if (/^\d{2}\.\d{2}\.\d{4}$/.test(datePart)) {
                const [day, month, year] = datePart.split('.');
                return `${year}-${month}-${day}`; // Переворачиваем в "2022-12-23"
            }

            // Если формат неожиданный, возвращаем пустую строку, чтобы не ломать input
            return '';
        };

        if (deviceType === 'Aksioma01') {
            setFactoryNumber(settings.serial_number || '');
            setCrc16(settings.settings_crc || '');
            setLastSettingsChange(formatDate(settings.lastModDatetime));
            setJumperPosition(settings.switch_save === 'Разрешено' ? 'Изменения разрешены' : 'Изменения запрещены');
            if (settings.sis_count_si && settings.sis_count_ft) setAlgorithmFormula(`СИ №${settings.sis_count_si}, ФТ №${settings.sis_count_ft}`);
        } else if (deviceType === 'Aksioma03') {
            setFactoryNumber(settings.serial_number || '');
            setCrc16(settings.settingsCRC16 ? formatHex(settings.settingsCRC16) : '');
            setLastSettingsChange(formatDate(settings.lastModDateTime));
            setJumperPosition(settings.switchSaveButton === 1 ? 'Изменения разрешены' : 'Изменения запрещены');

            //тут нужно вставить Алгоритм учета


        } else if (deviceType === 'Karat307') {
            setFactoryNumber(settings.serialNumber || '');
            setCrc16(settings.configChecksum || '');
            setLastSettingsChange(formatDate(settings.lastEditTime));
            setJumperPosition('Изменения запрещены');
            
        }
    };

    const formatHex = (value, padding = 4) => {
        if (value === null || value === undefined) return '';
        return '0x' + Number(value).toString(16).toUpperCase().padStart(padding, '0');
    };

    const handleSave = () => {
        if (isHeatComputer) {
            if (selectedDeviceIndex === -1 || !deviceType) {
                toast.warning('Выберите устройство из списка');
                return;
            }
            onSave({
                deviceType,
                deviceModel: deviceModel || null,
                deviceName: deviceName || null,
                displayName: displayName || null,
                factoryNumber: factoryNumber || null,
                verificationDate: verificationDate || null,
                verificationValidUntil: validUntil || null,
                releaseDate: releaseDate || null,
                crc16: crc16 || null,
                lastSettingsChange: lastSettingsChange || null,
                jumperPosition: jumperPosition || null,
                algorithmFormula: algorithmFormula || null,
                coldSourceParam: coldSourceParam || null,
                archiveDepth: archiveDepth || null
            });
        } else {
            onSave({
                displayName: null,
                factoryNumber: factoryNumber || null,
                verificationDate: verificationDate || null,
                verificationValidUntil: validUntil || null,
                position: position || null,
                deviceType: sensorDeviceType || null,
                deviceModel: sensorDeviceModel || null,
                specs: sensorSpecs || null
            });
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800 z-10">
                    <h3 className="text-white font-semibold">Добавить: {typeNames[type]}</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>

                <div className="p-4 space-y-4">
                    {isHeatComputer && (
                        <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/30">
                            <h4 className="text-blue-300 font-medium text-sm mb-3 flex items-center gap-2">
                                <Info className="w-4 h-4" />
                                Выберите существующее устройство
                            </h4>
                            {isLoadingDevices ? (
                                <div className="flex items-center gap-2 text-slate-400 text-sm">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Загрузка списка устройств...
                                </div>
                            ) : availableDevices.length === 0 ? (
                                <div className="text-slate-400 text-sm bg-slate-900/50 p-3 rounded border border-slate-700">
                                    По этому адресу нет доступных устройств для выбора.
                                </div>
                            ) : (
                                <select
                                    value={selectedDeviceIndex}
                                    onChange={handleDeviceSelect}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    {availableDevices.map((d, idx) => (
                                        <option key={idx} value={idx}>
                                            {DEVICE_TYPE_MAP[d.deviceType] || d.deviceType} — {d.deviceName || 'Без названия'}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}

                    {/* ✅ Поля Наименование в системе и Исполнение (Модель) только для чтения */}
                    {isHeatComputer && selectedDeviceIndex !== -1 && (
                        <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                            <h4 className="text-white font-medium text-sm mb-3">Информация об устройстве</h4>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Наименование в системе</label>
                                    <input
                                        type="text"
                                        value={deviceName}
                                        readOnly
                                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 text-sm cursor-not-allowed"
                                    />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Исполнение (Модель)</label>
                                    <input
                                        type="text"
                                        value={deviceModel}
                                        readOnly
                                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 text-sm cursor-not-allowed"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {isSensor && (
                        <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                            <h4 className="text-white font-medium text-sm mb-3">Характеристики устройства</h4>
                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Тип</label>
                                    <input type="text" value={sensorDeviceType} onChange={(e) => setSensorDeviceType(e.target.value)}
                                        placeholder={placeholders.type || 'Тип устройства'}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Исполнение (Модель)</label>
                                    <input type="text" value={sensorDeviceModel} onChange={(e) => setSensorDeviceModel(e.target.value)}
                                        placeholder={placeholders.model || 'Модель устройства'}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-slate-400 text-xs mb-1">Технические характеристики</label>
                                <textarea value={sensorSpecs} onChange={(e) => setSensorSpecs(e.target.value)}
                                    placeholder={placeholders.specs || 'Технические характеристики'}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm min-h-[100px] resize-y whitespace-pre-wrap" />
                            </div>
                        </div>
                    )}

                    <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                        <h4 className="text-white font-medium text-sm mb-3">Основная информация</h4>
                        <div className="grid grid-cols-2 gap-3">
                            {!isSensor && (
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Описание</label>
                                    <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                                </div>
                            )}
                            <div>
                                <label className="block text-slate-400 text-xs mb-1">Заводской номер</label>
                                <input type="text" value={factoryNumber} onChange={(e) => setFactoryNumber(e.target.value)}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                            </div>
                            <div>
                                <label className="block text-slate-400 text-xs mb-1">Дата поверки</label>
                                <input type="date" value={verificationDate} onChange={(e) => setVerificationDate(e.target.value)}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer" />
                            </div>
                            <div>
                                <label className="block text-slate-400 text-xs mb-1">Поверка действительна до</label>
                                <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer" />
                            </div>
                            {isSensor && (
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Переменная</label>
                                    <select value={position} onChange={(e) => setPosition(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm">
                                        <option value="">— Выберите —</option>
                                        {positionOptions[type]?.map(pos => (
                                            <option key={pos} value={pos}>{pos}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>

                    {isHeatComputer && (
                        <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                            <h4 className="text-white font-medium text-sm mb-3">Параметры программирования</h4>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Дата выпуска</label>
                                    <input type="date" value={releaseDate} onChange={(e) => setReleaseDate(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer" />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">CRC16 настроек</label>
                                    <input type="text" value={crc16} onChange={(e) => setCrc16(e.target.value)}
                                        placeholder="0x2DAC"
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Дата посл. изм. настроек</label>
                                    <input type="date" value={lastSettingsChange} onChange={(e) => setLastSettingsChange(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer" />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Перемычка</label>
                                    <select value={jumperPosition} onChange={(e) => setJumperPosition(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm">
                                        <option value="Изменения запрещены">Изменения запрещены</option>
                                        <option value="Изменения разрешены">Изменения разрешены</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Глубина архивов</label>
                                    <input type="text" value={archiveDepth} onChange={(e) => setArchiveDepth(e.target.value)}
                                        placeholder="4 года"
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                                </div>
                            </div>
                            <div className="space-y-3 mt-3">
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Алгоритм учета</label>
                                    <input type="text" value={algorithmFormula} onChange={(e) => setAlgorithmFormula(e.target.value)}
                                        placeholder="M1(h1-hx) - M2(h2-hx)"
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-xs mb-1">Параметр холодного источника</label>
                                    <input type="text" value={coldSourceParam} onChange={(e) => setColdSourceParam(e.target.value)}
                                        placeholder="6,5 °C"
                                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="px-4 py-3 border-t border-slate-700 flex justify-end gap-2 sticky bottom-0 bg-slate-800">
                    <button onClick={onClose}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm">Отмена</button>
                    <button onClick={handleSave} disabled={isHeatComputer && selectedDeviceIndex === -1}
                        className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                        <Plus className="w-4 h-4" /> Создать
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// ✅ Модальное окно опломбирования
// ============================================
const EditPlombModal = ({ node, onClose, onSave }) => {
    const [isOplombirovan, setIsOplombirovan] = useState(node.isOplombirovan);
    const [oplombirovanDate, setOplombirovanDate] = useState(
        node.oplombirovanDate ? node.oplombirovanDate.split('.').reverse().join('-') : ''
    );
    const [validUntil, setValidUntil] = useState(
        node.oplombirovanValidUntil ? node.oplombirovanValidUntil.split('.').reverse().join('-') : ''
    );

    const handleSave = () => {
        onSave({
            isOplombirovan,
            oplombirovanDate: oplombirovanDate || null,
            oplombirovanValidUntil: validUntil || null
        });
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-md">
                <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
                    <h3 className="text-white font-semibold">Опломбирование: {node.name}</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-4 space-y-4">
                    <label className="flex items-center gap-2 text-white">
                        <input type="checkbox" checked={isOplombirovan}
                            onChange={(e) => setIsOplombirovan(e.target.checked)}
                            className="w-4 h-4" /> Опломбирован
                    </label>
                    <div>
                        <label className="block text-slate-400 text-xs mb-1">Дата опломбирования</label>
                        <input type="date" value={oplombirovanDate}
                            onChange={(e) => setOplombirovanDate(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                    </div>
                    <div>
                        <label className="block text-slate-400 text-xs mb-1">Действительно до</label>
                        <input type="date" value={validUntil}
                            onChange={(e) => setValidUntil(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                    </div>
                </div>
                <div className="px-4 py-3 border-t border-slate-700 flex justify-end gap-2">
                    <button onClick={onClose}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm">Отмена</button>
                    <button onClick={handleSave}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm flex items-center gap-2">
                        <Save className="w-4 h-4" /> Сохранить
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// ✅ Модальное окно создания/редактирования узла
// ============================================
const NodeFormModal = ({ mode, node, onClose, onSave }) => {
    const toast = useToast();
    const [name, setName] = useState(node?.name || '');
    const [description, setDescription] = useState(node?.description || '');
    const [responsibleOrg, setResponsibleOrg] = useState(node?.responsibleOrg || '');
    const [responsibleName, setResponsibleName] = useState(node?.responsibleName || '');
    const [managementOrg, setManagementOrg] = useState(node?.managementOrg || '');
    const [managementName, setManagementName] = useState(node?.managementName || '');

    const handleSave = () => {
        if (!name.trim()) { toast.warning('Введите наименование узла'); return; }
        onSave({
            name: name.trim(),
            description: description.trim() || null,
            responsibleOrg: responsibleOrg.trim() || null,
            responsibleName: responsibleName.trim() || null,
            managementOrg: managementOrg.trim() || null,
            managementName: managementName.trim() || null
        });
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800 z-10">
                    <h3 className="text-white font-semibold">
                        {mode === 'create' ? 'Создать узел учёта' : 'Редактировать узел'}
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>

                <div className="p-4 space-y-4">
                    <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                        <h4 className="text-white font-medium text-sm mb-3">Основная информация</h4>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-slate-400 text-xs mb-1">
                                    Наименование узла <span className="text-red-400">*</span>
                                </label>
                                <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                                    placeholder="Узел учёта №1 (подвал)" autoFocus />
                            </div>
                            <div>
                                <label className="block text-slate-400 text-xs mb-1">Описание</label>
                                <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm min-h-[60px] resize-y"
                                    placeholder="Центральный тепловой пункт" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                        <h4 className="text-white font-medium text-sm mb-3">Ответственные</h4>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-slate-400 text-xs mb-1">Организация (ответственный)</label>
                                <input type="text" value={responsibleOrg} onChange={(e) => setResponsibleOrg(e.target.value)}
                                    placeholder='ООО "НПО Автоматика-Сервис"'
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                            </div>
                            <div>
                                <label className="block text-slate-400 text-xs mb-1">ФИО ответственного</label>
                                <input type="text" value={responsibleName} onChange={(e) => setResponsibleName(e.target.value)}
                                    placeholder="Т. В. Кайсарова"
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                            </div>
                            <div>
                                <label className="block text-slate-400 text-xs mb-1">Управляющая организация (владелец)</label>
                                <input type="text" value={managementOrg} onChange={(e) => setManagementOrg(e.target.value)}
                                    placeholder='ООО "УК Златоград"'
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                            </div>
                            <div>
                                <label className="block text-slate-400 text-xs mb-1">ФИО директора</label>
                                <input type="text" value={managementName} onChange={(e) => setManagementName(e.target.value)}
                                    placeholder="В. Ю. Сергеев"
                                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="px-4 py-3 border-t border-slate-700 flex justify-end gap-2 sticky bottom-0 bg-slate-800">
                    <button onClick={onClose}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm">Отмена</button>
                    <button onClick={handleSave}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm flex items-center gap-2">
                        <Save className="w-4 h-4" /> {mode === 'create' ? 'Создать' : 'Сохранить'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// ✅ Модальное окно подтверждения удаления узла
// ============================================
const DeleteConfirmModal = ({ nodeName, onClose, onConfirm }) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const handleDelete = async () => {
        setIsDeleting(true);
        try { await onConfirm(); }
        catch (err) { console.error(err); }
        finally { setIsDeleting(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-md">
                <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-400" /> Подтверждение удаления
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-4 space-y-3">
                    <p className="text-slate-300 text-sm">Вы действительно хотите удалить узел учёта?</p>
                    <div className="bg-slate-900 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                            <MeteringNodeIcon className="w-6 h-6 text-blue-400" />
                            <span className="text-white font-semibold">{nodeName}</span>
                        </div>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                        <p className="text-red-300 text-xs flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>
                                <strong>Внимание!</strong> Будут также удалены все связанные устройства:
                                тепловычислитель, датчики температуры, давления и расходомеры.
                                Это действие нельзя отменить.
                            </span>
                        </p>
                    </div>
                </div>
                <div className="px-4 py-3 border-t border-slate-700 flex justify-end gap-2">
                    <button onClick={onClose} disabled={isDeleting}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm disabled:opacity-50">Отмена</button>
                    <button onClick={handleDelete} disabled={isDeleting}
                        className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">
                        {isDeleting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Удаление...</>)
                            : (<><Trash2 className="w-4 h-4" /> Удалить</>)}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// ✅ Модальное окно подтверждения удаления устройства
// ============================================
const DeleteDeviceConfirmModal = ({ deviceName, deviceType, sectionType, onClose, onConfirm }) => {
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await onConfirm();
        } catch (err) {
            console.error(err);
        } finally {
            setIsDeleting(false);
        }
    };

    const SECTION_NAMES = {
        'heat-computer': 'Тепловычислитель',
        'temp-sensor': 'Датчик температуры',
        'pressure-sensor': 'Датчик давления',
        'flow-meter': 'Расходомер'
    };

    const DEVICE_MODELS = {
        'Aksioma01': 'Аксиома-01',
        'Aksioma03': 'Аксиома-03',
        'Karat307': 'Карат-307'
    };

    let displayText = 'Устройство';

    if (sectionType === 'heat-computer') {
        const modelName = DEVICE_MODELS[deviceType] || deviceType || 'Тепловычислитель';
        displayText = deviceName ? `${modelName} (${deviceName})` : modelName;
    } else {
        const sectionName = SECTION_NAMES[sectionType] || 'Устройство';
        displayText = deviceName ? `${sectionName} ${deviceName}` : sectionName;
    }

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-md">
                <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-400" /> Удалить устройство?
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-4 space-y-3">
                    <p className="text-slate-300 text-sm">Вы действительно хотите удалить это устройство?</p>
                    <div className="bg-slate-900 rounded-lg p-3">
                        <span className="text-white font-semibold">{displayText}</span>
                    </div>
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                        <p className="text-yellow-300 text-xs flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>Это действие нельзя отменить.</span>
                        </p>
                    </div>
                </div>
                <div className="px-4 py-3 border-t border-slate-700 flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        disabled={isDeleting}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm disabled:opacity-50"
                    >
                        Отмена
                    </button>
                    <button
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"
                    >
                        {isDeleting ? (
                            <><Loader2 className="w-4 h-4 animate-spin" /> Удаление...</>
                        ) : (
                            <><Trash2 className="w-4 h-4" /> Удалить</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// ✅ Экспорт компонента с ToastProvider
// ============================================
const HousePageWithToast = (props) => (
    <ToastProvider>
        <HousePage {...props} />
    </ToastProvider>
);

export default HousePageWithToast;